import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.js';
import { downloadUrl, getExtensionFromUrl } from '../lib/download.js';

export interface LightboxProps {
  urls: string[];
  initialIndex: number;
  onClose: () => void;
}

/** Fullscreen image viewer with keyboard nav and download, shared by the image and video job grids. */
export function ImageLightbox({ urls, initialIndex, onClose }: LightboxProps) {
  const [current, setCurrent] = useState(initialIndex);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') setCurrent((i) => (i > 0 ? i - 1 : urls.length - 1));
      if (event.key === 'ArrowRight') setCurrent((i) => (i < urls.length - 1 ? i + 1 : 0));
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, urls.length]);

  const url = urls[current] ?? '';
  const filename = `image-${current + 1}.${getExtensionFromUrl(url, 'image')}`;

  return (
    <div
      ref={overlayRef}
      className="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="lightbox-content">
        <button
          type="button"
          className="lightbox-close"
          aria-label="Close"
          onClick={onClose}
        >
          <Icon name="close" size={20} />
        </button>
        {urls.length > 1 && (
          <>
            <button
              type="button"
              className="lightbox-nav lightbox-prev"
              aria-label="Previous image"
              onClick={() => setCurrent((i) => (i > 0 ? i - 1 : urls.length - 1))}
            >
              <Icon name="back" size={24} />
            </button>
            <button
              type="button"
              className="lightbox-nav lightbox-next"
              aria-label="Next image"
              onClick={() => setCurrent((i) => (i < urls.length - 1 ? i + 1 : 0))}
            >
              <Icon name="chevronRight" size={24} />
            </button>
          </>
        )}
        <img
          className="lightbox-img"
          src={url}
          alt={`Generated image ${current + 1} of ${urls.length}`}
        />
        <div className="lightbox-actions">
          {urls.length > 1 && (
            <span className="lightbox-counter">{current + 1} / {urls.length}</span>
          )}
          <button
            type="button"
            className="btn btn-ghost lightbox-download"
            onClick={() => downloadUrl(url, filename)}
            aria-label="Download image"
          >
            <Icon name="download" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
